import type {
  AppointmentRow,
  CareKind,
  CareTask,
  ItemKind,
  PendingChat,
  PendingCounts,
  SupportTask,
} from "./api";

export type Severity = "critical" | "warning" | "muted";

export type Tier = "p0" | "p2";

export interface WorklistItem {
  key: string;
  /** which picard row this card is backed by — decides the actions we can offer */
  kind: ItemKind;
  tier: Tier;
  /** conversation id ("chat"), task id ("task") or appointment id ("appointment") */
  sourceId: number;
  name: string;
  mobile: string;
  severity: Severity;
  pill: string;
  reason: string;
  meta: string;
  /**
   * The at-a-glance scan anchor: the single number that says how bad this is
   * ("6h 12m", "1d", "today"). Rendered big + tabular so a CM can rank a screenful
   * of cards without reading a word.
   */
  urgencyValue: string;
  urgencyLabel: string;
  /** true = must be dealt with this shift. Drives the `?filter=urgent` view. */
  urgent: boolean;
  /** P0 only — minutes the customer has been waiting. Feeds "oldest wait". */
  waitingMins: number | null;
  /** P2 only — days until expiry (negative = already lapsed). */
  daysLeft: number | null;
  /**
   * Appointments only — the id of an EXISTING picard Snooze row for this appointment,
   * if it has ever been snoozed. picard never deletes the row, and CreateSnoozeView
   * rejects a second create, so this decides create-vs-edit (see api.snoozeAppointment).
   */
  snoozeId: number | null;
  /** sort key — higher first */
  rank: number;
}

/** picard's SLA windows, in minutes (bot/support_portal_views.py). */
export const SLA_MINUTES: Record<string, number> = {
  qna: 15,
  doctor_care: 25,
  followup: 60,
  lactation: 150,
};

/**
 * A customer expiring within this many days is still savable — surface them loudly.
 */
export const STILL_SAVABLE_DAYS = 2;

/**
 * Ignore retention tasks for customers who lapsed longer ago than this. Mirrors the
 * picard scan's look-back window. Prod carries stale `pending` tasks from 2024 that
 * nobody ever closed; they are not actionable work and must not bury live signals.
 */
export const STALE_LAPSED_DAYS = 30;

const MS_PER_MIN = 60_000;
const MS_PER_DAY = 86_400_000;

export function minutesSince(iso: string | null, now: number): number {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((now - t) / MS_PER_MIN));
}

export function formatWaiting(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

/** Compact form for the scan anchor — never wraps, always ≤4 chars-ish. */
function compactWaiting(mins: number): string {
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}

/** "13 Jul" — short, unambiguous, no year noise for near-term dates. */
export function formatDay(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
  });
}

function chatText(chat: PendingChat): string {
  const msg = chat.last_msg;
  if (!msg) return "No message content";
  return msg.content || msg.text || "No message content";
}

function prettyType(type: string): string {
  return type.replace(/_/g, " ");
}

/** P0 — actionable doctor-pending conversations, longest-waiting first. */
export function buildP0(chats: PendingChat[], now: number): WorklistItem[] {
  return chats
    .filter((c) => c.is_actionable && !c.is_snoozed)
    .map((chat) => {
      const sla = SLA_MINUTES[chat.type] ?? 15;
      const waiting = minutesSince(chat.updated_at ?? chat.created_date, now);
      const over = waiting - sla;
      // 2× the SLA window is our "this is now a complaint waiting to happen" line.
      const critical = waiting >= sla * 2;

      const askedOn = formatDay(chat.created_date);
      const meta = [
        `Waiting ${formatWaiting(waiting)}`,
        over > 0 ? `${formatWaiting(over)} past SLA` : "at SLA",
        chat.doctor_name ? `Dr ${chat.doctor_name} assigned` : "no doctor assigned",
        askedOn ? `asked ${askedOn}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

      return {
        key: `chat-${chat.id}`,
        kind: "chat" as const,
        tier: "p0" as const,
        sourceId: chat.conversation_id,
        name: chat.user_name || "Unknown customer",
        mobile: chat.user_mobile || "—",
        severity: (critical ? "critical" : "warning") as Severity,
        pill: `Unanswered · ${prettyType(chat.type)} · SLA ${sla} min`,
        reason: chatText(chat),
        meta,
        urgencyValue: compactWaiting(waiting),
        urgencyLabel: over > 0 ? "past SLA" : "waiting",
        urgent: critical,
        waitingMins: waiting,
        daysLeft: null,
        snoozeId: null,
        rank: waiting,
      };
    })
    .sort((a, b) => b.rank - a.rank);
}

/* ---------- P0 · appointments ---------- */

/**
 * DESIGN DECISION (deliberate, documented): an appointment that is imminent, overdue or
 * unconfirmed is SERVICE work — a customer is about to be, or already is, kept waiting.
 * That is the same failure mode as an unanswered question past its SLA, so it lives in
 * the SAME P0 tier rather than in a lane of its own. One tier, one ranking, one scan.
 *
 * To rank an appointment against a breached question we need one shared unit. We use
 * MINUTES OF LATENESS:
 *
 *     chat        rank = minutes the customer has been waiting for an answer
 *     appointment rank = 30 + minutes past the scheduled start (negative before it)
 *
 * So an appointment 30 minutes out ranks 0 (foot of P0), reaches 30 at the moment it is
 * due, and from there climbs minute-for-minute exactly like a waiting customer: an
 * appointment 30 min overdue (60) sits level with a question that has waited an hour.
 * Continuous, monotone, and it puts the SOONEST appointment next to the MOST-BREACHED
 * question — which is the whole point of a single pane.
 */
const APPT_DUE_CREDIT = 30;

/** "6:41 PM" — the only thing a CM needs to say on the phone. */
function formatTime(iso: string | null): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleTimeString("en-IN", {
    hour: "numeric",
    minute: "2-digit",
  });
}

function apptWhen(iso: string | null): string {
  const time = formatTime(iso);
  const day = formatDay(iso);
  if (!time) return "no time set";
  return day ? `${time}, ${day}` : time;
}

/** P0 — actionable appointments (picard's `is_actionable`), soonest/most-overdue first. */
export function buildAppointments(
  rows: AppointmentRow[],
  now: number
): WorklistItem[] {
  return rows.map((appt) => {
    const status = appt.appointment_status;
    const type = (appt.appointment_type || "consultation").replace(/_/g, " ");
    const sched = appt.scheduled_at;
    const parsed = sched ? Date.parse(sched) : NaN;
    // minutes until start; negative = the appointment is already overdue
    const minsUntil = Number.isNaN(parsed)
      ? null
      : Math.round((parsed - now) / MS_PER_MIN);
    const overdue = minsUntil !== null && minsUntil <= 0;
    const when = apptWhen(sched);
    const forChild = appt.patient_name ? ` for ${appt.patient_name}` : "";

    let pill: string;
    let reason: string;
    let urgencyLabel: string;
    if (status === "consultation_done") {
      pill = "Close-out pending";
      reason = `Consultation happened${forChild} and was never closed out.`;
      urgencyLabel = "to close";
    } else if (status === "on_going") {
      pill = "Consultation running long";
      reason = `Started ${when} and is still open — check the doctor finished the call.`;
      urgencyLabel = "running";
    } else if (status === "created" || status === "pending_confirmation") {
      const slot = appt.preferred_time || (sched ? formatTime(sched) : null);
      pill = slot ? `Unconfirmed · ${slot}` : "Unconfirmed · no time set";
      reason =
        status === "created"
          ? `Booked${forChild}, not confirmed — needs a slot and a call.`
          : `Awaiting confirmation${forChild} — the customer has not been called back.`;
      urgencyLabel = overdue ? "overdue" : "to appt";
    } else {
      pill = overdue
        ? `Appointment overdue · ${formatWaiting(Math.abs(minsUntil ?? 0))}`
        : `Appointment in ${formatWaiting(minsUntil ?? 0)}`;
      reason = overdue
        ? `Consultation${forChild} was due ${when} — nobody has started it.`
        : `Consultation${forChild} starts ${when}.`;
      urgencyLabel = overdue ? "overdue" : "to appt";
    }

    const meta = [
      appt.doctor_name ? `Dr ${appt.doctor_name} assigned` : "no doctor assigned",
      type,
      when,
    ].join(" · ");

    // Late, running long or never closed = a customer is already affected -> critical.
    // Still in the future = we can still be ready for them -> warning.
    const severity: Severity =
      overdue || status === "on_going" || status === "consultation_done"
        ? "critical"
        : minsUntil === null
        ? "critical"
        : "warning";

    return {
      key: `appointment-${appt.id}`,
      kind: "appointment" as const,
      tier: "p0" as const,
      sourceId: appt.id,
      name: appt.name || appt.patient_name || "Unknown customer",
      mobile: appt.phone_number || "—",
      severity,
      pill,
      reason,
      meta,
      urgencyValue:
        minsUntil === null ? "—" : compactWaiting(Math.abs(minsUntil)),
      urgencyLabel: minsUntil === null ? "no time" : urgencyLabel,
      // "this shift": already due, no time on file, or starting within two hours.
      urgent: minsUntil === null || minsUntil <= 120,
      waitingMins: null,
      daysLeft: null,
      snoozeId: typeof appt.snooze_id === "number" ? appt.snooze_id : null,
      rank: minsUntil === null ? APPT_DUE_CREDIT : APPT_DUE_CREDIT - minsUntil,
    };
  });
}

/* ---------- P0 · parents waiting (care flags) ---------- */

/**
 * The top lane on the board: a real parent messaged a Babynama WhatsApp line and is
 * waiting for a human RIGHT NOW — above retention, and ranked ahead of everything else
 * a CM does. Ordering:
 *
 *   longest wait first; at an EQUAL wait, medical outranks non-medical.
 *
 * (a sick baby cannot sit behind a billing query.) rank = wait*2 + medical-bonus keeps
 * the tiebreak from ever jumping a genuinely longer wait: two waits differ by ≥1 min ->
 * ≥2 apart, and the +1 medical bonus can only reorder a dead-heat.
 *
 * Wait comes off the flag's `wait_minutes` snapshot (set when picard flagged it), not
 * the wall clock — so the number the CM triages on is the number picard recorded.
 */
export interface CareWaitingItem {
  key: string;
  /** the PRIMARY flag's task id (highest-rank flag for this parent) */
  sourceId: number;
  /**
   * Every picard task id for this parent — the same person can message TWO Babynama
   * numbers and get two `unanswered_message` flags. We show one card; Dismiss must
   * close them ALL.
   */
  taskIds: number[];
  /**
   * The OTHER Babynama numbers this parent also wrote to, beyond the primary's —
   * de-duplicated, excluding the primary's own label. Empty for the common one-flag
   * case; drives the "· also on …" note on the card.
   */
  alsoOn: string[];
  kind: CareKind;
  mobile: string;
  /** which Babynama number they wrote to */
  sourceLabel: string;
  /** the parent's actual message */
  messageExcerpt: string;
  /** short triage reason, e.g. "asks how to book" */
  reason: string;
  waitMinutes: number;
  /** suggested reply to COPY — null for medical (no auto-draft for clinical questions) */
  draftReply: string | null;
  /** digits for the waplify ?phone= deep-link */
  deeplinkPhone: string;
  severity: Severity;
  /** kind badge text */
  kindLabel: string;
  urgencyValue: string;
  urgencyLabel: string;
  /** sort key — higher first */
  rank: number;
}

export function buildCareWaiting(tasks: CareTask[]): CareWaitingItem[] {
  // A per-task candidate carries every card field as-if it were the primary. We build
  // these first, then collapse to ONE card per parent (see below).
  type Candidate = Omit<CareWaitingItem, "key" | "taskIds" | "alsoOn">;

  const candidates: Candidate[] = tasks.map((task) => {
    const care = task.care;
    const medical = care.kind === "medical";
    const mins = Math.max(0, Math.round(care.wait_minutes));
    return {
      sourceId: task.id,
      kind: care.kind,
      mobile:
        task.all_details?.user_details?.user_mobile ||
        care.deeplink_phone ||
        "—",
      sourceLabel: care.source_label,
      messageExcerpt: care.message_excerpt || "No message content",
      reason: care.reason,
      waitMinutes: mins,
      draftReply: care.draft_reply,
      deeplinkPhone: care.deeplink_phone,
      severity: (medical ? "critical" : "warning") as Severity,
      kindLabel: medical ? "Medical" : "Non-medical",
      urgencyValue: compactWaiting(mins),
      urgencyLabel: "waiting",
      rank: mins * 2 + (medical ? 1 : 0),
    };
  });

  // ONE card per parent. The same person messaging two Babynama numbers produces two
  // `unanswered_message` flags — collapse them by `mobile` so the queue shows the
  // parent once. EDGE CASE: an unknown parent ("—"/empty) is NOT a real identity — do
  // NOT collapse distinct unknowns into one card; fall back to per-task grouping.
  const isUnknown = (m: string) => m === "—" || m === "";
  const groups = new Map<string, Candidate[]>();
  for (const c of candidates) {
    const groupKey = isUnknown(c.mobile) ? `task-${c.sourceId}` : c.mobile;
    const bucket = groups.get(groupKey);
    if (bucket) bucket.push(c);
    else groups.set(groupKey, [c]);
  }

  const items: CareWaitingItem[] = [];
  for (const bucket of groups.values()) {
    // Primary = highest-rank flag (longest wait; medical breaks a dead heat).
    const primary = bucket.reduce((best, c) => (c.rank > best.rank ? c : best));
    const unknown = isUnknown(primary.mobile);
    const alsoOn = unknown
      ? []
      : [
          ...new Set(
            bucket
              .filter((c) => c !== primary)
              .map((c) => c.sourceLabel)
          ),
        ].filter((label) => label !== primary.sourceLabel);
    items.push({
      ...primary,
      key: unknown ? `care-${primary.sourceId}` : `care-${primary.mobile}`,
      taskIds: bucket.map((c) => c.sourceId),
      alsoOn,
    });
  }

  return items.sort((a, b) => b.rank - a.rank);
}

/** P2 — subscription_expired retention tasks, soonest-expiring first. */
export function buildP2(tasks: SupportTask[], now: number): WorklistItem[] {
  return tasks
    // A snoozed task is one rescheduled into the future (see api.ts snoozeTask) —
    // it is not work for right now, so it leaves the queue until it comes due.
    .filter((task) => {
      if (!task.scheduled_at) return true;
      const due = Date.parse(task.scheduled_at);
      return Number.isNaN(due) || due <= now;
    })
    .map((task) => {
      const user = task.all_details?.user_details;
      const detail = task.all_details?.details?.[0];
      const endingAt = detail?.ending_at ?? null;
      const startedAt = detail?.started_at ?? null;

      // picard doesn't put days_remaining in remarks.system today — derive it
      // from the subscription's ending_at, and use remarks.system when present.
      const fromRemarks = task.remarks?.system?.days_remaining;
      const days =
        typeof fromRemarks === "number"
          ? fromRemarks
          : endingAt
          ? Math.ceil((Date.parse(endingAt) - now) / MS_PER_DAY)
          : null;

      let pill = "Subscription expired";
      if (days !== null) {
        if (days > 0) pill = `Expires in ${days} day${days === 1 ? "" : "s"}`;
        else if (days === 0) pill = "Expires today";
        else
          pill = `Lapsed ${Math.abs(days)} day${
            Math.abs(days) === 1 ? "" : "s"
          } ago`;
      }

      const plan = detail?.subscription_name || "Unknown plan";
      const savable = days !== null && days >= 0 && days <= STILL_SAVABLE_DAYS;

      // THE REASON LINE. It used to read "Subscription lifecycle — renewal at risk"
      // on every single card — the most valuable line on the card spent saying
      // nothing. It now carries what a CM actually needs to triage this customer
      // WITHOUT clicking: which plan (how much revenue), how long they have been a
      // customer (loyalty / how winnable), and the length of the cycle that is ending.
      // Everything here is real data off the task payload; we invent nothing.
      const cycleDays =
        startedAt && endingAt
          ? Math.max(
              1,
              Math.round((Date.parse(endingAt) - Date.parse(startedAt)) / MS_PER_DAY)
            )
          : null;
      const since = formatDay(startedAt);
      const reason = [
        `Plan ${plan}`,
        since ? `customer since ${since}` : null,
        cycleDays ? `${cycleDays}-day cycle` : null,
      ]
        .filter(Boolean)
        .join(" · ");

      const endDay = formatDay(endingAt);
      // `days` can come from remarks.system even when ending_at is absent — never
      // render "Ends null".
      const endsOn = endDay ? `Ends ${endDay}` : "End date not on file";
      const endedOn = endDay ? `Ended ${endDay}` : "End date not on file";
      let meta: string;
      if (days === null) {
        meta = endDay ? `Ends ${endDay}` : "No subscription dates on file";
      } else if (days > 0) {
        meta = `${endsOn} · ${days} day${days === 1 ? "" : "s"} left to save`;
      } else if (days === 0) {
        meta = `${endsOn} — last day to save this customer`;
      } else {
        meta = `${endedOn} · lapsed ${Math.abs(days)} day${
          Math.abs(days) === 1 ? "" : "s"
        } ago`;
      }

      let urgencyValue = "—";
      let urgencyLabel = "no date";
      if (days !== null) {
        if (days > 0) {
          urgencyValue = `${days}d`;
          urgencyLabel = "to expiry";
        } else if (days === 0) {
          urgencyValue = "0d";
          // NOT "expires today" — the P2 e2e asserts on /Expires (in \d+ days?|today)/
          // and that must resolve to the pill alone.
          urgencyLabel = "last day";
        } else {
          urgencyValue = `${Math.abs(days)}d`;
          urgencyLabel = "lapsed";
        }
      }

      return {
        key: `task-${task.id}`,
        kind: "task" as const,
        tier: "p2" as const,
        sourceId: task.id,
        name: user?.user_name || "Unknown customer",
        mobile: user?.user_mobile || "—",
        // "Expires today" is the last day we can still save them — that is a red
        // event, not a grey one. 1–2 days out stays amber; already-lapsed is muted.
        severity: (days === 0
          ? "critical"
          : savable
          ? "warning"
          : "muted") as Severity,
        pill,
        reason,
        meta,
        urgencyValue,
        urgencyLabel,
        urgent: savable,
        waitingMins: null,
        daysLeft: days,
        snoozeId: null,
        // Rank by SAVABILITY, not by how long ago they left:
        //   expiring (days >= 0) always outranks lapsed — you can still keep them,
        //   soonest-expiring first; among lapsed, the most RECENT lapse is the most
        //   winnable. (The naive `-days` sort put 2-year-old churn above a customer
        //   expiring today.)
        rank:
          days === null
            ? Number.NEGATIVE_INFINITY
            : days >= 0
            ? 1000 - days
            : 500 + days,
      };
    })
    // Drop stale tasks: pending rows for customers who lapsed long ago (prod has
    // 2024-era ones nobody ever closed). They are not actionable retention work.
    .filter((item) => item.daysLeft === null || item.daysLeft >= -STALE_LAPSED_DAYS)
    .sort((a, b) => b.rank - a.rank);
}

/* ---------- queue-level summary (the "what am I walking into" header) ---------- */

export interface WorklistSummary {
  total: number;
  urgent: number;
  expiringToday: number;
  /** minutes the longest-waiting P0 customer has been waiting; 0 when P0 is empty */
  oldestWaitMins: number;
}

export function summarize(
  p0: WorklistItem[],
  p2: WorklistItem[]
): WorklistSummary {
  const all = [...p0, ...p2];
  return {
    total: all.length,
    urgent: all.filter((i) => i.urgent).length,
    expiringToday: p2.filter((i) => i.daysLeft === 0).length,
    oldestWaitMins: p0.reduce((max, i) => Math.max(max, i.waitingMins ?? 0), 0),
  };
}

/* ---------- the operational glance (meaningful even at zero) ---------- */
// The old Home never went blank: it always showed pending-by-type, actionable appts,
// at-risk and doctor-reply health, so a CM knew the state of play even when nothing
// was breaching. These helpers rebuild that from the SAME picard data the queue uses.

export interface QuestionGlance {
  /** every doctor_pending conversation, breached or not */
  pending: number;
  /** the subset past its SLA window (snoozed breaches still count — still breached) */
  pastSla: number;
  /** the subset a CM deliberately parked */
  snoozed: number;
}

export function summarizeQuestionCounts(counts: PendingCounts): QuestionGlance {
  const buckets = [counts.qna, counts.followup, counts.doctor_care];
  return {
    pending: buckets.reduce((n, b) => n + b.pending, 0),
    pastSla: buckets.reduce((n, b) => n + b.actionable, 0),
    snoozed: buckets.reduce((n, b) => n + b.snoozed, 0),
  };
}

/** Retention tasks the queue is deliberately NOT showing — and why. */
export interface TaskGlance {
  /** rescheduled into the future (our task-snooze mechanism) — back when due */
  snoozed: number;
  /** lapsed longer than STALE_LAPSED_DAYS ago — dropped as non-actionable */
  stale: number;
}

export function taskGlance(tasks: SupportTask[], now: number): TaskGlance {
  let snoozed = 0;
  let stale = 0;
  for (const task of tasks) {
    if (task.scheduled_at) {
      const due = Date.parse(task.scheduled_at);
      if (!Number.isNaN(due) && due > now) {
        snoozed += 1;
        continue;
      }
    }
    const endingAt = task.all_details?.details?.[0]?.ending_at ?? null;
    const fromRemarks = task.remarks?.system?.days_remaining;
    const days =
      typeof fromRemarks === "number"
        ? fromRemarks
        : endingAt
        ? Math.ceil((Date.parse(endingAt) - now) / MS_PER_DAY)
        : null;
    if (days !== null && days < -STALE_LAPSED_DAYS) stale += 1;
  }
  return { snoozed, stale };
}

/* ---------- snoozed work (discoverable, never actionable from here) ---------- */

/**
 * A deliberately SLIM row — display + a customer link only. No snooze/mark-done:
 * re-snoozing from here would need the create-vs-edit dance for chats too, and a
 * parked item's actions live on the live card once its snooze ends. Nothing is lost,
 * nothing new can fire.
 */
export interface SnoozedRowData {
  key: string;
  kind: ItemKind;
  name: string;
  mobile: string;
  /** what it is + how it stood when parked — enough to decide "leave it" or not */
  detail: string;
}

export function buildSnoozedRows(
  chats: PendingChat[],
  appts: AppointmentRow[],
  now: number
): SnoozedRowData[] {
  const chatRows = chats
    .filter((c) => c.is_snoozed)
    .map((chat) => ({
      key: `snoozed-chat-${chat.id}`,
      kind: "chat" as const,
      name: chat.user_name || "Unknown customer",
      mobile: chat.user_mobile || "—",
      detail: `${prettyType(chat.type)} question · waiting ${formatWaiting(
        minutesSince(chat.updated_at ?? chat.created_date, now)
      )}`,
    }));
  const apptRows = appts
    .filter((a) => a.is_snoozed)
    .map((appt) => ({
      key: `snoozed-appointment-${appt.id}`,
      kind: "appointment" as const,
      name: appt.name || appt.patient_name || "Unknown customer",
      mobile: appt.phone_number || "—",
      detail: `${(appt.appointment_type || "consultation").replace(/_/g, " ")} · ${apptWhen(
        appt.scheduled_at
      )}`,
    }));
  return [...chatRows, ...apptRows];
}

/* ---------- contextual empty states ---------- */
// "Nothing needs you right now" told a CM nothing. An empty tier now says WHY it is
// empty, with the live numbers — reassurance they can verify, not a blank.

function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/**
 * P0 is empty. If the glance counts loaded, explain the quiet; if they did not,
 * fall back to the old line rather than invent numbers.
 * Note: when P0 is empty, every past-SLA question is by definition snoozed
 * (an unsnoozed breach would be ON the queue) — the copy can say so safely.
 */
export function describeQuietP0(
  questions: QuestionGlance | null,
  snoozedAppointments: number
): string {
  if (!questions) return "Nothing needs you right now.";
  const q =
    questions.pending === 0
      ? "0 questions pending"
      : questions.pastSla === 0
      ? `${plural(questions.pending, "question")} pending — all inside SLA`
      : `${plural(questions.pending, "question")} pending · ${
          questions.pastSla
        } past SLA, snoozed`;
  const a =
    snoozedAppointments > 0
      ? plural(snoozedAppointments, "appointment") + " snoozed"
      : "no appointments need action";
  return `Nothing is breaching — ${q} · ${a}.`;
}

/** P2 is empty: nothing in the window, and here is what the queue is holding back. */
export function describeQuietP2(glance: TaskGlance): string {
  const held: string[] = [];
  if (glance.snoozed > 0)
    held.push(`${plural(glance.snoozed, "task")} snoozed until later`);
  if (glance.stale > 0)
    held.push(
      `${glance.stale} stale (lapsed over ${STALE_LAPSED_DAYS}d ago) hidden`
    );
  const base = "No subscriptions expiring or recently lapsed";
  return held.length ? `${base} · ${held.join(" · ")}.` : `${base}.`;
}
